/**
 * UPS API Service
 * Handles all UPS API interactions including OAuth 2.0 authentication and shipping operations
 */

import { getAdminAccessToken, getAdminApiBaseUrl } from '@/lib/adminApi';
interface UPSAuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
}

interface UPSShipmentRequest {
  shipper: {
    name: string;
    attentionName: string;
    phone: string;
    address: {
      addressLine: string[];
      city: string;
      stateProvinceCode: string;
      postalCode: string;
      countryCode: string;
    };
  };
  shipTo: {
    name: string;
    attentionName: string;
    phone: string;
    address: {
      addressLine: string[];
      city: string;
      stateProvinceCode: string;
      postalCode: string;
      countryCode: string;
    };
  };
  package: {
    weight: string;
    weightUnit: 'LBS' | 'KGS';
    dimensions?: {
      length: string;
      width: string;
      height: string;
      unit: 'IN' | 'CM';
    };
  };
  service: {
    code: string; // e.g., "03" for UPS Ground, "01" for UPS Next Day Air
    description: string;
  };
}

interface UPSShipmentResponse {
  shipmentIdentificationNumber: string;
  trackingNumber: string;
  labelImageFormat: string;
  graphicImage: string; // Base64 encoded label
  totalCharges: {
    monetaryValue: string;
    currencyCode: string;
  };
}

class UPSService {
  private baseUrl: string;
  private clientId: string;
  private clientSecret: string;
  private accountNumber: string;
  private accessToken: string | null = null;
  private tokenExpiry: number | null = null;

  constructor() {
    // Check if we're in test/sandbox mode or production
    const isProduction = process.env.REACT_APP_UPS_ENVIRONMENT === 'production';
    
    this.baseUrl = isProduction
      ? 'https://onlinetools.ups.com/api'
      : 'https://wwwcie.ups.com/api';
    
    this.clientId = process.env.REACT_APP_UPS_CLIENT_ID || '';
    this.clientSecret = process.env.REACT_APP_UPS_CLIENT_SECRET || '';
    this.accountNumber = process.env.REACT_APP_UPS_ACCOUNT_NUMBER || '';

    console.log('UPS Service Initialized:', {
      environment: process.env.REACT_APP_UPS_ENVIRONMENT || 'sandbox',
      baseUrl: this.baseUrl,
      hasClientId: !!this.clientId,
      hasClientSecret: !!this.clientSecret,
      hasAccountNumber: !!this.accountNumber,
      clientIdPrefix: this.clientId ? this.clientId.substring(0, 10) + '...' : 'MISSING',
    });

    if (!this.clientId || !this.clientSecret || !this.accountNumber) {
      console.error('UPS credentials not configured! Please set environment variables:', {
        REACT_APP_UPS_CLIENT_ID: this.clientId ? 'SET' : 'MISSING',
        REACT_APP_UPS_CLIENT_SECRET: this.clientSecret ? 'SET' : 'MISSING',
        REACT_APP_UPS_ACCOUNT_NUMBER: this.accountNumber ? 'SET' : 'MISSING',
      });
    }
  }

  /**
   * Get OAuth 2.0 access token from UPS
   */
  private async getAccessToken(): Promise<string> {
    // Return cached token if still valid
    if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.accessToken;
    }

    try {
      const credentials = btoa(`${this.clientId}:${this.clientSecret}`);
      
      const response = await fetch(`${this.baseUrl}/security/v1/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${credentials}`,
          'x-merchant-id': this.accountNumber,
        },
        body: 'grant_type=client_credentials',
      });

      if (!response.ok) {
        throw new Error(`UPS OAuth failed: ${response.status} ${response.statusText}`);
      }

      const data: UPSAuthResponse = await response.json();
      
      this.accessToken = data.access_token;
      // Set expiry to 5 minutes before actual expiry for safety
      this.tokenExpiry = Date.now() + (data.expires_in - 300) * 1000;
      
      return this.accessToken;
    } catch (error) {
      console.error('Failed to get UPS access token:', error);
      throw error;
    }
  }

  /**
   * Create a shipping label
   */
  async createShipment(shipmentData: UPSShipmentRequest): Promise<UPSShipmentResponse> {
    console.log('🚀 createShipment called!');
    try {
      const authToken = await getAdminAccessToken();

      // Call our backend API instead of UPS directly
      // Backend expects simplified data structure
      const apiBaseUrl = getAdminApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/v1/shipping/labels`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(shipmentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `API request failed: ${response.status}`);
      }

      const data = await response.json();
      
      // Backend returns: { success: true, data: { shipmentId, trackingNumber, labelImage, totalCharges } }
      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to create label');
      }
      
      return {
        shipmentIdentificationNumber: data.data.shipmentId,
        trackingNumber: data.data.trackingNumber,
        labelImageFormat: data.data.labelImageFormat,
        graphicImage: data.data.labelImage,
        totalCharges: {
          monetaryValue: data.data.totalCharges.monetaryValue,
          currencyCode: data.data.totalCharges.currencyCode,
        },
      };
    } catch (error) {
      console.error('Failed to create UPS shipment:', error);
      throw error;
    }
  }

  /**
   * Track a shipment by tracking number
   */
  async trackShipment(trackingNumber: string): Promise<any> {
    try {
      const token = await this.getAccessToken();

      const response = await fetch(
        `${this.baseUrl}/track/v1/details/${trackingNumber}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'transId': `krosskast-track-${Date.now()}`,
            'transactionSrc': 'KrossKast Admin',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`UPS Tracking API failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to track UPS shipment:', error);
      throw error;
    }
  }

  /**
   * Validate an address
   */
  async validateAddress(address: {
    addressLine: string[];
    city: string;
    stateProvinceCode: string;
    postalCode: string;
    countryCode: string;
  }): Promise<any> {
    try {
      const token = await this.getAccessToken();

      const response = await fetch(`${this.baseUrl}/addressvalidation/v1/1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'transId': `krosskast-validate-${Date.now()}`,
          'transactionSrc': 'KrossKast Admin',
        },
        body: JSON.stringify({
          AddressValidationRequest: {
            Request: {
              RequestOption: '1',
            },
            Address: {
              AddressLine: address.addressLine,
              City: address.city,
              StateProvinceCode: address.stateProvinceCode,
              PostalCode: address.postalCode,
              CountryCode: address.countryCode,
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`UPS Address Validation failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to validate address:', error);
      throw error;
    }
  }
}

export default new UPSService();
export type { UPSShipmentRequest, UPSShipmentResponse };

